import FeaturedCarousel from "./FeaturedCarousel";
import stampsIcon from "../../assets/Stamps.png";
import fdcsIcon from "../../assets/FDCs.png";
import articlesIcon from "../../assets/Articles.png";
import calendarIcon from "../../assets/Calendar.png";

export default function Hero({ stats, featured }) {
  // Icon heights share one scale factor (height = intrinsic px × 34/994) so
  // the identical stroke weight in the source files renders identically.
  // pt/pb are tuned per row (font metrics differ per row) so the rendered
  // gap between row text and each divider is 16px — the mosaic tile gap.
  // iconNudge shifts each icon (not the text, which would disturb those
  // divider gaps) by the measured gap between the icon's ink center and
  // the text block's ink center — fractional line-box heights and each
  // PNG's own proportions leave every row with a different sub-pixel gap.
  const statRows = [
    {
      value: stats?.stamps,
      label: "Stamps",
      icon: stampsIcon,
      iconH: 34,
      iconNudge: -1,
      pt: 12,
      pb: 8,
    },
    {
      value: stats?.covers,
      label: "First-day covers",
      icon: fdcsIcon,
      iconH: 24,
      iconNudge: 1,
      pt: 8.5,
      pb: 8,
    },
    {
      value: stats?.articles,
      label: "Articles",
      icon: articlesIcon,
      iconH: 33,
      iconNudge: 0,
      pt: 9,
      pb: 7.5,
    },
    {
      value:
        stats?.minYear && stats?.maxYear
          ? `${stats.minYear}–${stats.maxYear}`
          : null,
      label: "Years spanning the archive",
      icon: calendarIcon,
      iconH: 29,
      iconNudge: 0.5,
      pt: 9.5,
      pb: 12,
    },
  ].filter((row) => row.value !== null && row.value !== undefined);

  const hasCarousel = featured.length > 0;

  // pt/pb make the RENDERED whitespace 40px on both edges — 40px from the
  // header rule down to the heading's ink (the h1 line box adds ~12px above
  // the caps) and 40px from the year-span label's ink down to the divider
  // (row pb 12 + section pb 22 + ~6px of line box under the label)
  return (
    <section className="max-w-[1287px] mx-auto px-6 pt-[28px] pb-[22px]">
      {/* grid-cols-1 (minmax(0,1fr)) lets the track shrink below the
          carousel image's intrinsic width, matching the lg column minimums */}
      <div
        className={`grid grid-cols-1 gap-10 lg:gap-2 items-center ${
          hasCarousel ? "lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]" : ""
        }`}
      >
        {/* Text column */}
        <div>
          <h1 className="font-display text-seal text-5xl sm:text-6xl leading-[1.05]">
            A country
            <br />
            in miniature.
          </h1>
          <p className="font-serif text-[rgb(37,34,33)] text-base leading-relaxed max-w-sm mt-[13px]">
            Explore Japan&rsquo;s history, culture, and natural beauty through
            the art and stories of its postal heritage.
          </p>

          <dl className="mt-[11px] max-w-sm">
            {statRows.map((row, i) => (
              <div
                key={row.label}
                className={`flex items-center gap-5 ${
                  i > 0 ? "border-t border-ink/10" : ""
                }`}
                style={{ paddingTop: row.pt, paddingBottom: row.pb }}
              >
                <span
                  className="relative w-9 shrink-0 flex items-center justify-center"
                  style={{ top: row.iconNudge }}
                >
                  <img
                    src={row.icon}
                    alt=""
                    style={{ height: row.iconH }}
                    className="w-auto object-contain"
                  />
                </span>
                {/* Font metrics sink the visible glyphs ~4px below the flex
                    center; nudge so the text block centers on the icon */}
                <div className="relative -top-1">
                  <dd className="font-display text-xl text-[rgb(37,34,33)] whitespace-nowrap">
                    {typeof row.value === "number"
                      ? row.value.toLocaleString()
                      : row.value}
                  </dd>
                  <dt className="font-mono text-[11px] uppercase tracking-[0.18em] text-[rgb(37,34,33)] mt-0.5">
                    {row.label}
                  </dt>
                </div>
              </div>
            ))}
          </dl>
        </div>

        {/* Featured carousel — hidden on mobile */}
        {hasCarousel && (
          <div className="hidden md:block">
            <FeaturedCarousel items={featured} />
          </div>
        )}
      </div>
    </section>
  );
}
